import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { retryOnAnimationFrames, type AnimationFrameRetry } from '@util'
import { isDefined } from '@terros/common'
import {
  createPointerHandlers,
  createGreedyWheelHandler,
  focusedNodeSelector,
  panShiftToReveal,
  stepZoomAtCenter,
  toFitZoom,
  toSubpixelPan,
  ZOOM_STEP,
  type ViewportDragState,
  type ViewportPan,
  type ViewportPointerInput,
} from './graphViewportHandlers'

const RECENTER_DURATION_MS = 300

const PANEL_WIDTH_TRANSITION_SETTLE_MS = 500

const offsetToCenterWithoutClipping = (viewportSize: number, contentSize: number): number =>
  Math.max(0, Math.round((viewportSize - contentSize) / 2))

const REVEAL_MARGIN_PX = 16

type UseGraphViewportArgs = {
  focusId: string | null
  hasContent: boolean
  recenterKey: string
  panelOpen: boolean
  revealId?: string | null
}

export type GraphViewport = {
  containerRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  zoom: number
  pan: ViewportPan
  isPanning: boolean
  layoutReady: boolean
  onPointerDown: (event: ViewportPointerInput) => void
  onPointerMove: (event: ViewportPointerInput) => void
  onPointerEnd: (event: ViewportPointerInput) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
}

export const useGraphViewport = ({
  focusId,
  hasContent,
  recenterKey,
  panelOpen,
  revealId,
}: UseGraphViewportArgs): GraphViewport => {
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState<ViewportPan>({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [layoutReady, setLayoutReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const panRef = useRef<ViewportPan>(pan)
  const zoomRef = useRef(zoom)
  const recenterFrameRef = useRef<number | null>(null)
  const resizeTrackerRef = useRef<ResizeObserver | null>(null)
  const focusRetryRef = useRef<AnimationFrameRetry | null>(null)
  const didPlaceViewportRef = useRef(false)
  const dragRef = useRef<ViewportDragState | null>(null)

  const commitPan = useCallback((next: ViewportPan): void => {
    panRef.current = next
    setPan(next)
  }, [])

  const commitZoom = useCallback((next: number): void => {
    zoomRef.current = next
    setZoom(next)
  }, [])

  const cancelRecenter = useCallback((): void => {
    if (recenterFrameRef.current === null) return
    cancelAnimationFrame(recenterFrameRef.current)
    recenterFrameRef.current = null
  }, [])

  const stopResizeTracking = useCallback((): void => {
    resizeTrackerRef.current?.disconnect()
    resizeTrackerRef.current = null
  }, [])

  const stopFocusRetry = useCallback((): void => {
    focusRetryRef.current?.cancel()
    focusRetryRef.current = null
  }, [])

  const onManualInteraction = useCallback((): void => {
    didPlaceViewportRef.current = true
    stopFocusRetry()
    stopResizeTracking()
    cancelRecenter()
  }, [cancelRecenter, stopFocusRetry, stopResizeTracking])

  const animatePanTo = useCallback(
    (target: ViewportPan): void => {
      const from = panRef.current
      const start = performance.now()

      const tick = (now: number): void => {
        const progress = Math.min((now - start) / RECENTER_DURATION_MS, 1)
        const eased = 1 - (1 - progress) ** 3
        commitPan({
          x: toSubpixelPan(from.x + (target.x - from.x) * eased),
          y: toSubpixelPan(from.y + (target.y - from.y) * eased),
        })
        recenterFrameRef.current = progress < 1 ? requestAnimationFrame(tick) : null
      }

      recenterFrameRef.current = requestAnimationFrame(tick)
    },
    [commitPan]
  )

  const applyFit = useCallback((): boolean => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return false

    const fitZoom = toFitZoom({ container, content })
    if (!isDefined(fitZoom)) return false

    commitZoom(fitZoom)
    commitPan({
      x: offsetToCenterWithoutClipping(container.offsetWidth, content.offsetWidth * fitZoom),
      y: offsetToCenterWithoutClipping(container.offsetHeight, content.offsetHeight * fitZoom),
    })
    return true
  }, [commitPan, commitZoom])

  const centerContentOnce = useCallback((): boolean => {
    if (didPlaceViewportRef.current) return true
    if (!applyFit()) return false
    didPlaceViewportRef.current = true
    return true
  }, [applyFit])

  const revealWithoutCentering = useCallback((): void => setLayoutReady(true), [])

  const revealCenteredContent = useCallback((): boolean => {
    if (!centerContentOnce()) return false
    setLayoutReady(true)
    return true
  }, [centerContentOnce])

  useEffect(() => {
    setLayoutReady(false)
    if (!hasContent) {
      didPlaceViewportRef.current = false
      return
    }
    const retry = retryOnAnimationFrames({
      attempt: revealCenteredContent,
      onExhausted: revealWithoutCentering,
    })
    return retry.cancel
  }, [hasContent, revealCenteredContent, revealWithoutCentering])

  const panNodeIntoView = useCallback(
    (nodeDomId: string): boolean => {
      const container = containerRef.current
      const content = contentRef.current
      if (!container || !content) return false

      const node = content.querySelector(focusedNodeSelector(nodeDomId))
      if (!node) return false

      const shift = panShiftToReveal({
        nodeBounds: node.getBoundingClientRect(),
        containerBounds: container.getBoundingClientRect(),
        margin: REVEAL_MARGIN_PX,
      })
      if (!shift) return true

      cancelRecenter()
      animatePanTo({
        x: toSubpixelPan(panRef.current.x + shift.x),
        y: toSubpixelPan(panRef.current.y + shift.y),
      })
      return true
    },
    [animatePanTo, cancelRecenter]
  )

  const centerFocusedNode = useCallback((): boolean => {
    if (!isDefined(focusId)) return true
    return panNodeIntoView(focusId)
  }, [focusId, panNodeIntoView])

  useEffect(() => {
    const container = containerRef.current
    if (!container || !isDefined(focusId) || !layoutReady) return

    focusRetryRef.current = retryOnAnimationFrames({ attempt: centerFocusedNode })
    const tracker = new ResizeObserver(centerFocusedNode)
    resizeTrackerRef.current = tracker
    tracker.observe(container)
    if (contentRef.current) tracker.observe(contentRef.current)
    const settleTimer = setTimeout(stopResizeTracking, PANEL_WIDTH_TRANSITION_SETTLE_MS)

    return () => {
      clearTimeout(settleTimer)
      stopFocusRetry()
      stopResizeTracking()
      cancelRecenter()
    }
  }, [
    cancelRecenter,
    centerFocusedNode,
    focusId,
    layoutReady,
    panelOpen,
    recenterKey,
    stopFocusRetry,
    stopResizeTracking,
  ])

  useEffect(() => {
    if (!isDefined(revealId) || !layoutReady) return
    const retry = retryOnAnimationFrames({ attempt: () => panNodeIntoView(revealId) })
    return retry.cancel
  }, [layoutReady, panNodeIntoView, revealId])

  const pointerHandlers = createPointerHandlers({
    dragRef,
    panRef,
    setPan: commitPan,
    setIsPanning,
    onInteractionStart: onManualInteraction,
  })

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const handler = createGreedyWheelHandler({
      containerRef,
      zoomRef,
      panRef,
      setZoom: commitZoom,
      setPan: commitPan,
      onInteractionStart: onManualInteraction,
    })
    element.addEventListener('wheel', handler, { passive: false })
    return () => element.removeEventListener('wheel', handler)
  }, [commitPan, commitZoom, onManualInteraction])

  const stepZoom = useCallback(
    (delta: number): void => {
      const container = containerRef.current
      if (!container) return
      onManualInteraction()
      const next = stepZoomAtCenter({ container, zoom: zoomRef.current, pan: panRef.current, delta })
      commitZoom(next.zoom)
      commitPan(next.pan)
    },
    [commitPan, commitZoom, onManualInteraction]
  )

  const zoomIn = useCallback((): void => stepZoom(ZOOM_STEP), [stepZoom])
  const zoomOut = useCallback((): void => stepZoom(-ZOOM_STEP), [stepZoom])

  const resetZoom = useCallback((): void => {
    onManualInteraction()
    applyFit()
  }, [applyFit, onManualInteraction])

  return {
    containerRef,
    contentRef,
    zoom,
    pan,
    isPanning,
    layoutReady,
    ...pointerHandlers,
    zoomIn,
    zoomOut,
    resetZoom,
  }
}
